/**
 * Article link builder for the admin copy / preview / list / WhatsApp links.
 *
 * Mirror of suprabhaatham-nextjs/src/utils/articleSlug.ts — keep the two in sync
 * (same slug rules). Every News article whose admin-entered permalink gives a
 * usable slug has the canonical URL `/details/<id>/<slug>`; one without a usable
 * permalink keeps the bare `/details/<id>`. There is no id or date threshold. A
 * mismatch with the website is harmless (the website redirects to its canonical
 * URL); it only costs the reader one extra hop. The permalink is never generated
 * or written back.
 */
import { API } from "../config/API";

const MAX_SLUG_LENGTH = 80;
const MIN_SLUG_LENGTH = 3;

export const articleSlug = (permalink?: string | null): string => {
  const raw = (permalink || "").trim();
  // Percent-encoded / non-ASCII permalinks (the 2016-2020 archive) are not usable.
  if (!raw || /%|[^\x00-\x7f]/.test(raw)) return "";
  let slug = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (slug.length > MAX_SLUG_LENGTH) {
    const head = slug.slice(0, MAX_SLUG_LENGTH + 1);
    const cut = head.lastIndexOf("-");
    slug = (cut > 0 ? head.slice(0, cut) : slug.slice(0, MAX_SLUG_LENGTH)).replace(/-+$/g, "");
  }
  return slug.length >= MIN_SLUG_LENGTH ? slug : "";
};

/**
 * Full public URL of a News article, for Copy / Preview / WhatsApp text
 * (API.APP_URL ends with a slash): the slug URL when the permalink is usable,
 * otherwise the bare URL. Never `?link=`, `undefined` or `null`.
 */
export const articleLink = (
  id: number | string,
  attrs?: { permalink?: string | null } | null
): string => {
  const bare = `${API.APP_URL}details/${id}`;
  const slug = articleSlug(attrs?.permalink);
  return slug ? `${bare}/${slug}` : bare;
};
